import React from 'react';

import { cn } from 'domain/core';
import {
	useComponentDefaultProps,
	useMove,
	UseMovePosition,
	useUncontrolled,
} from 'domain/hooks';
import { getPosition, mergeRefs } from 'domain/utils';
import { DivProps } from './common-types';
import { Interactive } from './InteractiveElement';
import { Key } from './Keyboard';

import {
	getChangeValue,
	getClientPosition,
	Slider,
	SliderProps,
	SliderTypes,
} from './Slider';

const getArrayEntryOrDefault = <T extends any>(
	arr: T[] = [],
	index: number
): T | undefined => {
	if (arr && Array.isArray(arr)) {
		if (index === 1) {
			if (arr.length >= 1) {
				return arr[1];
			} else {
				return arr[0];
			}
		} else {
			return arr[0];
		}
	}
};

export namespace RangeSliderTypes {
	export type Bar = SliderTypes.Bar;
	export type Progress = ProgressType;
	export type Thumb = SliderTypes.Thumb;
	export type ThumbLabel = SliderTypes.ThumbLabel;
}

type Value = [number, number];
interface RangeSliderProps
	extends Omit<
		SliderProps,
		| 'value'
		| 'onChange'
		| 'onChangeComplete'
		| 'defaultValue'
		| 'classNames'
		| 'components'
	> {
	value?: Value;
	defaultValue?: Value;
	onChange?: (value: Value) => void;
	onChangeComplete?: (value: Value) => void;
	minRange?: number;
	maxRange?: number;
	marks?: SliderTypes.Mark[];
	classNames?: {
		Thumbs?: string[];
		Bar?: string;
		ThumbLabel?: string[];
		Progress?: string;
		Marks?: string;
	};
	components?: {
		Thumbs?: SliderTypes.Thumb[];
		Bar?: SliderTypes.Bar;
		ThumbLabel?: SliderTypes.ThumbLabel[];
		Progress?: ProgressType;
		Marks?: SliderTypes.Marks;
	};
}

const defaultProps: RangeSliderProps = {
	barSize: 8,
	defaultValue: [0, 100],
	min: 0,
	max: 100,
	step: 1,
	direction: 'horizontal',
	formatLabelValue: (val: number) => val,
	components: {},
	classNames: {
		Bar: '',
		Progress: '',
		Thumbs: [],
		ThumbLabel: [],
		Marks: '',
	},
};

const Root = React.forwardRef((props: RangeSliderProps, ref: any) => {
	const _props = useComponentDefaultProps(defaultProps, props);

	const [value, handleOnChange] = useUncontrolled<Value>({
		defaultValue: _props.defaultValue!,
		value: _props.value,
		onChange: _props.onChange,
		finalValue: [_props.min!, _props.max!],
	});

	const valueRef = React.useRef<Value>(value);

	const thumbs = React.useRef<HTMLDivElement[]>([]);
	const thumbIndex = React.useRef<number>(0);

	const positions: [number, number] = React.useMemo(
		() => [
			getPosition({ value: value[0], min: _props.min!, max: _props.max! }),
			getPosition({ value: value[1], min: _props.min!, max: _props.max! }),
		],
		[value, _props.min!, _props.max!]
	);

	const handleThumbMouseDownAndFocus = React.useCallback(
		(index: number) => (evt: any) => {
			evt.stopPropagation();
			thumbIndex.current = index;
		},
		[]
	);

	const _setValue = (val: Value) => {
		handleOnChange(val);
		valueRef.current = val;
	};

	React.useEffect(
		() => {
			if (Array.isArray(_props.value)) {
				valueRef.current = _props.value;
			}
		},
		Array.isArray(_props.value)
			? [_props.value[0], _props.value[1]]
			: [null, null]
	);

	const setRangedValue = (
		val: number,
		index: number,
		triggerChangeEnd: boolean
	) => {
		const clone: Value = [...valueRef.current];
		const {
			max,
			min,
			maxRange = 100,
			minRange = 1,
			onChangeComplete,
		} = _props as Required<RangeSliderProps>;
		clone[index] = val;

		if (index === 0) {
			if (val > clone[1] - (minRange - 0.000000001)) {
				clone[1] = Math.min(val + minRange, max);
			}

			if (val > (max - (minRange - 0.000000001) || min)) {
				clone[index] = valueRef.current[index];
			}

			if (clone[1] - val > maxRange) {
				clone[1] = val + maxRange;
			}
		}

		if (index === 1) {
			if (val < clone[0] + minRange) {
				clone[0] = Math.max(val - minRange, min);
			}

			if (val < clone[0] + minRange) {
				clone[index] = valueRef.current[index];
			}

			if (val - clone[0] > maxRange) {
				clone[0] = val - maxRange;
			}
		}

		console.log(clone);

		_setValue(clone);

		if (triggerChangeEnd) {
			onChangeComplete?.(valueRef.current);
		}
	};

	const handleChange = ({ x, y }: UseMovePosition) => {
		if (!_props.disabled) {
			const { max, min, step, precision, direction } =
				_props as Required<RangeSliderProps>;

			const val = direction === 'horizontal' ? x : 1 - y;

			const nextValue = getChangeValue({
				value: val,
				min,
				max,
				step,
				precision,
			});
			setRangedValue(nextValue, thumbIndex.current, false);
		}
	};

	const handleTrackMouseDownCapture = React.useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
		) => {
			barRef.current!.focus();
			const rect = barRef.current!.getBoundingClientRect();
			const changePosition = getClientPosition(event.nativeEvent);

			let changePositionVal, rectSide, dimension;
			if (_props.direction === 'horizontal') {
				changePositionVal = changePosition[0];
				rectSide = rect.left;
				dimension = rect.width;
			} else {
				changePositionVal = changePosition[1];
				rectSide = rect.bottom;
				dimension = rect.height;
			}

			const { max, min, step } = _props as Required<RangeSliderProps>;

			const changeValue = getChangeValue({
				value: Math.abs(changePositionVal - rectSide),
				max,
				min,
				step,
				containerWidth: dimension,
			});

			const nearestHandle =
				Math.abs(value[0] - changeValue) > Math.abs(value[1] - changeValue)
					? 1
					: 0;

			thumbIndex.current = nearestHandle;
		},
		[_props.direction, _props.min, _props.max, _props.step, value]
	);

	const handleScrubEnd = React.useCallback(() => {
		_props.onChangeComplete?.(valueRef.current);
	}, [_props.onChangeComplete]);

	const { ref: barRef, active } = useMove(handleChange, {
		onScrubEnd: handleScrubEnd,
	});

	const handleTrackKeydownCapture = React.useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (!_props.disabled) {
				const { max, min, step } = _props as Required<RangeSliderProps>;
				const focusedIndex = thumbIndex.current;
				switch (event.key as Key) {
					case Key.ArrowUp:
					case Key.ArrowRight: {
						event.preventDefault();
						thumbs.current[focusedIndex].focus();
						setRangedValue(
							Math.min(
								Math.max(valueRef.current[focusedIndex] + step, min),
								max
							),
							focusedIndex,
							true
						);
						break;
					}
					case Key.ArrowDown:
					case Key.ArrowLeft: {
						event.preventDefault();
						thumbs.current[focusedIndex].focus();
						setRangedValue(
							Math.min(
								Math.max(valueRef.current[focusedIndex] - step, min),
								max
							),
							focusedIndex,
							true
						);
						break;
					}
					default: {
						break;
					}
				}
			}
		},
		[_props.disabled, _props.max, _props.min, _props.step]
	);

	const handleBarFocus = React.useCallback(
		() => thumbs.current[thumbIndex.current]?.focus(),
		[]
	);

	const {
		_Bar,
		_Progress,
		_ThumbA,
		_ThumbB,
		_ThumbLabelA,
		_ThumbLabelB,
		_Marks,
	} = React.useMemo(
		() => ({
			_Bar: _props.components!.Bar ?? Slider.Bar,
			_Progress: _props.components?.Progress ?? Progress,
			_ThumbA:
				getArrayEntryOrDefault(_props.components?.Thumbs, 0) ?? Slider.Thumb,
			_ThumbB:
				getArrayEntryOrDefault(_props.components?.Thumbs, 0) ?? Slider.Thumb,

			_ThumbLabelA:
				getArrayEntryOrDefault(_props.components?.ThumbLabel, 0) ??
				Slider.ThumbLabel,
			_ThumbLabelB:
				getArrayEntryOrDefault(_props.components?.ThumbLabel, 0) ??
				Slider.ThumbLabel,
			_Marks: _props.components!.Marks ?? Slider.Marks,
		}),
		[_props.components]
	);

	return (
		<Interactive onKeyDown={handleTrackKeydownCapture}>
			<_Bar
				barSize={_props.barSize!}
				direction={_props.direction!}
				className={_props.classNames?.Bar}
				ref={mergeRefs(barRef, ref)}
				onFocus={handleBarFocus}
				onMouseDown={handleTrackMouseDownCapture}>
				<_Progress
					barSize={_props.barSize!}
					positions={positions}
					direction={_props.direction!}
					className={_props.classNames?.Progress}
				/>

				<_Marks
					marks={_props.marks}
					barSize={_props.barSize!}
					direction={_props.direction!}
					max={_props.max!}
					min={_props.min!}
					className={_props.classNames?.Marks}
				/>

				<_ThumbA
					barSize={_props.barSize!}
					active={active && thumbIndex.current === 0}
					position={positions[0]}
					className={_props.classNames?.Thumbs?.[0]}
					onMouseDown={handleThumbMouseDownAndFocus(0)}
					ref={(ref) => {
						if (ref) {
							thumbs.current[0] = ref;
						}
					}}
					onFocus={handleThumbMouseDownAndFocus(0)}
					direction={_props.direction!}>
					<_ThumbLabelA
						direction={_props.direction!}
						position={positions[0]}
						active={active && thumbIndex.current === 0}
						className={_props.classNames?.ThumbLabel?.[0]}>
						{_props.formatLabelValue!(value[0])}
					</_ThumbLabelA>
				</_ThumbA>
				<_ThumbB
					barSize={_props.barSize!}
					active={active && thumbIndex.current === 1}
					position={positions[1]}
					className={_props.classNames?.Thumbs?.[1]}
					onMouseDown={handleThumbMouseDownAndFocus(1)}
					ref={(ref) => {
						if (ref) {
							thumbs.current[1] = ref;
						}
					}}
					direction={_props.direction!}
					onFocus={handleThumbMouseDownAndFocus(1)}>
					<_ThumbLabelB
						direction={_props.direction!}
						position={positions[1]}
						active={active && thumbIndex.current === 1}
						className={_props.classNames?.ThumbLabel?.[1]}>
						{_props.formatLabelValue!(value[1])}
					</_ThumbLabelB>
				</_ThumbB>
				<input type='hidden' value={value[0]} />
				<input type='hidden' value={value[1]} />
			</_Bar>
		</Interactive>
	);
});

interface ProgressProps extends DivProps {
	positions: [number, number];
	barSize: number;
	direction: SliderTypes.Direction;
}
type ProgressType = React.ForwardRefExoticComponent<
	ProgressProps & React.RefAttributes<HTMLDivElement>
>;
const Progress: ProgressType = React.forwardRef(
	({ className, positions, barSize, direction, ...props }, ref) => {
		return (
			<div
				className={cn('absolute rounded-full bg-primary-300', className)}
				{...props}
				ref={ref}
				style={
					direction === 'horizontal'
						? {
								height: barSize,
								width: `${positions[1] - positions[0]}%`,
								left: `${positions[0]}%`,
						  }
						: {
								height: `${positions[1] - positions[0]}%`,
								width: barSize,
								left: 0,
								bottom: `${positions[0]}%`,
						  }
				}
			/>
		);
	}
);

export const RangeSlider = {
	Root,
};
